import React from 'react';
import { Car, Bike, Info } from 'lucide-react';
import { PersonalInfo } from './types';

interface PersonalInfoStepProps {
  vehicleType: 'auto' | 'motorcycle';
  personalInfo: PersonalInfo;
  setPersonalInfo: (info: PersonalInfo) => void;
  isOwnerSameAsDriver: boolean;
  setIsOwnerSameAsDriver: (value: boolean) => void;
}

export default function PersonalInfoStep({
  vehicleType,
  personalInfo,
  setPersonalInfo,
  isOwnerSameAsDriver,
  setIsOwnerSameAsDriver
}: PersonalInfoStepProps) {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4 mb-6">
        {vehicleType === 'auto' ? (
          <Car className="h-8 w-8 text-primary-500" />
        ) : (
          <Bike className="h-8 w-8 text-primary-500" />
        )}
        <h3 className="text-xl font-semibold text-white">
          Vehicle Owner Information - {vehicleType === 'auto' ? 'Auto' : 'Motorcycle'}
        </h3>
      </div>

      <div className="bg-blue-900/20 border border-blue-800/30 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-blue-300 text-sm font-medium">About This Section</p>
            <p className="text-blue-200 text-sm mt-1">
              This section is for the <strong>vehicle owner's information</strong>, which may be different from the driver. 
              If you are both the owner and driver, enter your information in both sections.
            </p>
          </div>
        </div>
      </div>

      {/* Same as Driver Checkbox */}
      <div className="mb-6">
        <label className="flex items-center space-x-3">
          <input
            type="checkbox"
            checked={isOwnerSameAsDriver}
            onChange={(e) => setIsOwnerSameAsDriver(e.target.checked)}
            className="form-checkbox h-5 w-5 text-primary-600 rounded border-gray-700 focus:ring-primary-500"
          />
          <span className="text-white font-medium">Owner is the same as driver (auto-fill information)</span>
        </label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Owner's Full Name *
          </label>
          <input
            type="text"
            value={personalInfo.fullName}
            onChange={(e) => setPersonalInfo({ ...personalInfo, fullName: e.target.value })}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Address *
          </label>
          <input
            type="text"
            value={personalInfo.address}
            onChange={(e) => setPersonalInfo({ ...personalInfo, address: e.target.value })}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            ID Number *
          </label>
          <input
            type="text"
            value={personalInfo.idNumber}
            onChange={(e) => setPersonalInfo({ ...personalInfo, idNumber: e.target.value })}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Date of Birth *
          </label>
          <input
            type="date"
            value={personalInfo.dateOfBirth}
            onChange={(e) => setPersonalInfo({ ...personalInfo, dateOfBirth: e.target.value })}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Nationality
          </label>
          <input
            type="text"
            value={personalInfo.nationality}
            onChange={(e) => setPersonalInfo({ ...personalInfo, nationality: e.target.value })}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Country
          </label>
          <input
            type="text"
            value={personalInfo.country}
            onChange={(e) => setPersonalInfo({ ...personalInfo, country: e.target.value })}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Mobile Phone *
          </label>
          <input
            type="tel"
            value={personalInfo.mobilePhone}
            onChange={(e) => setPersonalInfo({ ...personalInfo, mobilePhone: e.target.value })}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Email
          </label>
          <input
            type="email"
            value={personalInfo.email}
            onChange={(e) => setPersonalInfo({ ...personalInfo, email: e.target.value })}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
            disabled
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            WhatsApp
          </label>
          <input
            type="tel"
            value={personalInfo.whatsapp}
            onChange={(e) => setPersonalInfo({ ...personalInfo, whatsapp: e.target.value })}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Representative of the Owner (Driver)
          </label>
          <input
            type="text"
            value={personalInfo.representativeOfOwner}
            onChange={(e) => setPersonalInfo({ ...personalInfo, representativeOfOwner: e.target.value })}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
          />
        </div>
      </div>
    </div>
  );
} 